import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChainContext } from '../contexts/ChainContext';
import { Search } from 'lucide-react';
import BlockchainService from '../services/blockchainService';
import debounce from 'lodash/debounce';
import { isPublicKey } from '../utils/publicKey';

// Antelope transaction ids are 64-char hex hashes (SHA-256). Account names are
// a-z1-5 (max 13 chars), so they never collide — we can route by format safely.
const isTransactionId = (term) =>
  /^(0x)?[0-9a-fA-F]{64}$/.test(term);

// A plain integer is treated as a block number (account names are never all-numeric).
const isBlockNumber = (term) => /^\d+$/.test(term);

// variant: 'hero' (portada, más alto) | 'compact' (banda del resto de páginas)
// true en pantallas ≥ 640px (para acortar el placeholder en móvil)
const useIsWide = () => {
  const query = '(min-width: 640px)';
  const [wide, setWide] = useState(() => typeof window === 'undefined' || !window.matchMedia || window.matchMedia(query).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = (e) => setWide(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange));
  }, []);
  return wide;
};

const AccountSearch = ({ variant = 'compact' }) => {
  const isHero = variant === 'hero';
  const isWide = useIsWide();
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [resolving, setResolving] = useState(false);
  const [notFoundId, setNotFoundId] = useState(null);
  const { currentChain } = useChainContext();
  const suggestionsRef = useRef(null);
  const blockchainService = useRef(new BlockchainService(currentChain));
  const navigate = useNavigate();

  useEffect(() => {
    blockchainService.current = new BlockchainService(currentChain);
  }, [currentChain]);

  const debouncedSearch = useRef(
    debounce(async (search) => {
      // Transaction ids, block numbers and public keys are resolved directly on Enter, not via autocomplete.
      if (isTransactionId(search) || isBlockNumber(search) || isPublicKey(search)) {
        setSuggestions([]);
        return;
      }
      if (search.length >= 3) {
        try {
          
          const response = await blockchainService.current.searchAccounts(search);
          
          const accounts = response.rows.map(row => row.scope);
          setSuggestions(accounts);
          
          // New: Auto-redirect if there's only one result
          if (accounts.length === 1) {
            navigate(`/account-detail/${accounts[0]}`);
            setSearchTerm('');
            setSuggestions([]);
          }
        } catch (error) {
          console.error('Error searching accounts:', error);
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
      }
    }, 300)
  ).current;

  useEffect(() => {
    debouncedSearch(searchTerm);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    const term = searchTerm.trim();
    if (!term) return;
    setNotFoundId(null);

    // A 64-hex could be a transaction id OR a block hash (same SHA-256 format) —
    // resolve it server-side before navigating.
    if (isTransactionId(term)) {
      const clean = term.replace(/^0x/, '');
      setResolving(true);
      try {
        const result = await blockchainService.current.resolveHash(clean);
        if (result.type === 'transaction') {
          navigate(`/transaction/${result.id}`);
        } else if (result.type === 'block') {
          navigate(`/block/${result.block_num}`);
        } else {
          setNotFoundId(clean);
        }
      } catch (error) {
        console.error('Error resolving hash:', error);
        setNotFoundId(clean);
      } finally {
        setResolving(false);
        setSearchTerm('');
        setSuggestions([]);
      }
      return;
    }

    if (isBlockNumber(term)) {
      navigate(`/block/${term}`);
    } else if (isPublicKey(term)) {
      navigate(`/key/${term}`);
    } else {
      navigate(`/account-detail/${term}`);
    }
    setSearchTerm('');
    setSuggestions([]);
  };

  const handleSuggestionClick = (suggestion) => {
    navigate(`/account-detail/${suggestion}`);
    setSearchTerm('');
    setSuggestions([]);
  };

  return (
    <form onSubmit={handleSearch} role="search" className={`relative w-full ${isHero ? 'max-w-[760px]' : 'max-w-2xl'}`}>
      <div className="flex bg-surface rounded-[10px] p-1">
        <label htmlFor={`search-${variant}`} className="sr-only">Search</label>
        <input
          id={`search-${variant}`}
          type="text"
          placeholder={isWide
            ? `Search by account, transaction id, block number or public key on ${blockchainService.current.getChainName()}`
            : 'Account, tx, block or key'}
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setNotFoundId(null); }}
          className={`flex-1 min-w-0 border-0 outline-none bg-transparent px-3.5 text-sm text-gray-900 placeholder-gray-400 ${isHero ? 'py-3' : 'py-2'}`}
        />
        <button
          type="submit"
          className={`btn-primary flex items-center justify-center shrink-0 ${isHero ? 'w-11' : 'w-10'}`}
          aria-label="Search"
        >
          <Search size={18} />
        </button>
      </div>
      {resolving && (
        <div className="absolute z-20 w-full bg-surface border border-gray-200 rounded-lg shadow-lg mt-1 px-4 py-2 text-sm text-gray-500">
          Resolving…
        </div>
      )}
      {!resolving && notFoundId && (
        <div className="absolute z-20 w-full bg-surface border border-red-200 rounded-lg shadow-lg mt-1 px-4 py-2 text-sm text-red-600">
          No transaction or block found for <span className="font-mono break-all">{notFoundId}</span>
        </div>
      )}
      {suggestions.length > 1 && (
        <ul ref={suggestionsRef} className="absolute z-20 w-full bg-surface border border-gray-200 rounded-lg shadow-lg mt-1 py-1">
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              className="px-4 py-2 hover:bg-gray-50 hover:text-indigo-700 cursor-pointer text-gray-700 text-sm"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </form>
  );
};

export default AccountSearch;